'use client';

import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { ListItem } from '@jouzen/ecom-components/v3/ListItem';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { TypographyRhythm } from '@jouzen/ecom-components/v3/TypographyRhythm';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { EventType } from '@/analytics/types';
import RichText from '@/app/components/RichText';
import useScrollTracking from '@/app/hooks/useScrollTracking';

interface TrackedLinkProps {
  readonly children: ReactNode;
  readonly href: string;
}

function TrackedLink({ children, href }: TrackedLinkProps) {
  const handleOnClick = () => {
    const pathname = new URL(href);
    void window.ouraAnalytics.track(EventType.LinkClicked, {
      cta: 'link clicked',
      location: 'cookie policy',
      path: pathname,
    });
  };

  return (
    <BodyLink href={href} target="_blank" onClick={handleOnClick}>
      {children}
    </BodyLink>
  );
}

function createLink(href: string) {
  return function CreatedLink(chunks: ReactNode) {
    return <TrackedLink href={href}>{chunks}</TrackedLink>;
  };
}

const dataProtectionLink = () => (
  <TrackedLink href="mailto:dataprotection@ouraring.com">
    dataprotection@ouraring.com
  </TrackedLink>
);
const allAboutCookiesLink = () => (
  <TrackedLink href="https://www.allaboutcookies.org">
    https://allaboutcookies.org
  </TrackedLink>
);
const ourSiteLink = () => (
  <TrackedLink href="https://ouraring.com">https://ouraring.com</TrackedLink>
);

const CookiePolicy = () => {
  const t = useTranslations();
  useScrollTracking();

  return (
    <LayoutGrid data-cy="cookie-policy">
      <TypographyRhythm className="col-main">
        <Typography Element="h1" variant="h2">
          {t('cookie_policy_title')}
        </Typography>

        {/* Section One */}
        <Typography Element="h2" variant="h4">
          {t('introduction')}
        </Typography>
        <Typography>
          <RichText
            messageKey="cookie_policy_intro_one"
            tValues={{
              about: createLink('/about-us'),
            }}
          />
        </Typography>
        <Typography>
          <RichText messageKey="cookie_policy_intro_two" />
        </Typography>
        <Typography>
          <RichText
            messageKey="cookie_policy_intro_three"
            tValues={{
              privacy: createLink('https://ouraring.com/privacy-policy'),
              terms: createLink('https://ouraring.com/terms-and-conditions'),
            }}
          />
        </Typography>

        {/* Section Two */}
        <Typography Element="h2" variant="h4">
          {t('our_site')}
        </Typography>
        <Typography>
          <RichText
            messageKey="cookie_policy_our_site_copy"
            tValues={{
              link: ourSiteLink,
            }}
          />
        </Typography>

        {/* Section Three */}
        <Typography Element="h2" variant="h4">
          {t('what_cookies_are')}
        </Typography>
        <Typography>{t('cookie_policy_what_cookies_are_copy')}</Typography>

        {/* Section Four */}
        <Typography Element="h2" variant="h4">
          {t('why_we_use_cookies')}
        </Typography>
        <Typography>{t('cookie_policy_why_we_use_cookies_copy')}</Typography>
        <Typography>
          {t('cookie_policy_why_we_use_cookies_list_title')}
        </Typography>
        <div className="flex flex-col gap-2">
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_one')}</ListItem>
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_two')}</ListItem>
          <ListItem>
            {t('cookie_policy_why_we_use_cookies_list_three')}
          </ListItem>
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_four')}</ListItem>
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_five')}</ListItem>
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_six')}</ListItem>
          <ListItem>
            {t('cookie_policy_why_we_use_cookies_list_seven')}
          </ListItem>
        </div>

        {/* Section Five */}
        <Typography Element="h2" variant="h4">
          {t('types_of_cookies')}
        </Typography>
        <Typography>{t('cookie_policy_types_copy_one')}</Typography>
        <Typography>{t('cookie_policy_types_list_title')}</Typography>
        <div className="flex flex-col gap-2">
          <ListItem>
            <RichText messageKey="cookie_policy_types_list_one" />
          </ListItem>
          <ListItem>
            <RichText messageKey="cookie_policy_types_list_two" />
          </ListItem>
          <ListItem>
            <RichText messageKey="cookie_policy_types_list_three" />
          </ListItem>
          <ListItem>
            <RichText messageKey="cookie_policy_types_list_four" />
          </ListItem>
        </div>
        <Typography>
          <RichText
            messageKey="cookie_policy_types_copy_two"
            tValues={{
              tool: createLink('https://optout.aboutads.info/'),
              union: createLink('https://www.youronlinechoices.eu/'),
            }}
          />
        </Typography>

        {/* Section Six */}
        <Typography Element="h2" variant="h4">
          {t('cookie_policy_consent_to_use')}
        </Typography>
        <Typography>{t('cookie_policy_consent_copy')}</Typography>

        {/* Section Seven */}
        <Typography Element="h2" variant="h4">
          {t('managing_cookies')}
        </Typography>
        <Typography>{t('cookie_policy_managing_cookies_copy_one')}</Typography>
        <Typography>
          <RichText
            messageKey="cookie_policy_managing_cookies_copy_two"
            tValues={{
              link: allAboutCookiesLink,
            }}
          />
        </Typography>
        <Typography>
          {t('cookie_policy_managing_cookies_copy_three')}
        </Typography>
        <div className="flex flex-col gap-2">
          <ListItem>
            <TrackedLink href="https://optout.networkadvertising.org/?c=1">
              Network Advertising Initiative
            </TrackedLink>{' '}
            (NAI)
          </ListItem>
          <ListItem>
            <TrackedLink href="https://optout.aboutads.info/?c=2&lang=EN">
              Digital Advertising Alliance
            </TrackedLink>{' '}
            (DAA)
          </ListItem>
          <ListItem>
            <TrackedLink href="https://www.youronlinechoices.com/">
              European Interactive Digital Advertising Alliance
            </TrackedLink>{' '}
            (EDAA)
          </ListItem>
          <ListItem>
            <TrackedLink href="https://youradchoices.ca/">
              Digital Advertising Alliance of Canada
            </TrackedLink>{' '}
            (DAAC)
          </ListItem>
        </div>
        <Typography>{t('cookie_policy_managing_cookies_copy_four')}</Typography>

        {/* Section Eight */}
        <Typography Element="h2" variant="h4">
          {t('your_privacy_rights')}
        </Typography>
        <Typography>{t('cookie_policy_private_rights_copy_one')}</Typography>
        <Typography>
          <RichText
            messageKey="cookie_policy_private_rights_copy_two"
            tValues={{
              link: dataProtectionLink,
            }}
          />
        </Typography>

        {/* Section Nine */}
        <Typography Element="h2" variant="h4">
          {t('how_to_contact_us')}
        </Typography>
        <Typography>
          <RichText
            messageKey="cookie_policy_contact_us_copy"
            tValues={{
              link: dataProtectionLink,
            }}
          />
        </Typography>

        {/* Section Ten */}
        <Typography Element="h2" variant="h4">
          {t('do_not_track_policy')}
        </Typography>
        <Typography>
          <RichText
            messageKey="cookie_policy_do_not_track_copy"
            tValues={{
              link: createLink('https://allaboutdnt.com/'),
            }}
          />
        </Typography>

        {/* Section Eleven */}
        <Typography Element="h2" variant="h4">
          {t('changes_to_this_policy')}
        </Typography>
        <Typography>{t('cookie_policy_changes_copy')}</Typography>
      </TypographyRhythm>
    </LayoutGrid>
  );
};

export default CookiePolicy;
